import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { toStorySlug } from '../../../lib/slugify';

interface TopWriter {
  id: string;
  display_name: string;
  avatar_url: string | null;
  bio: string | null;
  storyCount: number;
  totalLikes: number;
  totalReads: number;
}

const TopWritersSidebar = () => {
  const [writers, setWriters] = useState<TopWriter[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTopWriters = async () => {
      try {
        const { data, error } = await supabase.rpc('get_writers_with_stats');
        if (error) throw error;

        const sorted = (data || [])
          .map((w: any) => ({
            id:           w.id,
            display_name: w.display_name,
            avatar_url:   w.avatar_url,
            bio:          w.bio,
            storyCount:   Number(w.story_count),
            totalLikes:   Number(w.total_likes),
            totalReads:   Number(w.total_reads),
          }))
          .sort((a: TopWriter, b: TopWriter) => b.totalLikes - a.totalLikes)
          .slice(0, 5);

        setWriters(sorted);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTopWriters();
  }, []);

  return (
    <aside className="space-y-6">
      {/* Top Writers */}
      <div className="bg-[#0f0f0f] border border-red-900/20 rounded-lg p-5">
        <h3 className="text-base font-bold text-white font-['Cinzel'] mb-4 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center text-red-500">
            <i className="ri-quill-pen-fill" />
          </div>
          Top Writers
        </h3>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-9 h-9 rounded-full bg-gray-800 shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 bg-gray-800 rounded w-2/3" />
                  <div className="h-3 bg-gray-800 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : writers.length === 0 ? (
          <p className="text-gray-500 text-sm font-['Crimson_Text']">No writers yet.</p>
        ) : (
          <div className="space-y-3">
            {writers.map((w, i) => (
              <Link
                key={w.id}
                to={`/profile/writer/${w.id}`}
                className="flex items-center gap-3 group cursor-pointer"
              >
                <span className="text-xs text-gray-600 font-['Cinzel'] w-4 shrink-0">#{i + 1}</span>
                <div className="w-9 h-9 rounded-full overflow-hidden shrink-0 bg-gradient-to-br from-red-900 to-red-700 flex items-center justify-center">
                  {w.avatar_url ? (
                    <img src={w.avatar_url} alt={w.display_name} className="w-full h-full object-cover" />
                  ) : (
                    <i className="ri-user-line text-white text-sm" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-['Crimson_Text'] group-hover:text-red-400 transition-colors truncate">
                    {w.display_name}
                  </p>
                  <p className="text-xs text-gray-500 font-['Crimson_Text']">
                    {w.storyCount} {w.storyCount === 1 ? 'story' : 'stories'} · {w.totalLikes.toLocaleString()} likes
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}

        <Link
          to="/writers"
          className="mt-4 flex items-center gap-1 text-xs text-red-500 hover:text-red-400 font-['Crimson_Text'] transition-colors cursor-pointer"
        >
          View all writers <i className="ri-arrow-right-line" />
        </Link>
      </div>
    </aside>
  );
};

export default TopWritersSidebar;
